import { useState, useCallback, useRef, useEffect } from 'react';
import { Question, AnswerState, PracticeSessionResult } from '@/types/exam';
import { api } from '@/services/api';
import { mapQuestion } from '@/lib/api-adapters';
import { getContentLocale } from '@/lib/contentLocale';
import { shuffleArray } from '@/lib/utils';

type SessionState = 'loading' | 'pending-resume' | 'answering' | 'showing-result' | 'completed';

interface UsePracticeSessionOptions {
  examId: string;
  categoryId: string;
  shuffle?: boolean;
  difficulty?: 'easy' | 'medium' | 'hard';
  count?: number;
}

export interface SavedPracticeSession {
  examId: string;
  categoryId: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  count: number;
  questions: Question[];
  currentIndex: number;
  results: PracticeSessionResult[];
  selectedAnswer: number | null;
  answerState: AnswerState;
  sessionState: 'answering' | 'showing-result';
  sessionStartTime: number;
  questionStartTime: number;
  lastSaved: number;
}

export function usePracticeSession({
  examId,
  categoryId,
  shuffle = true,
  difficulty,
  count,
}: UsePracticeSessionOptions) {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sessionState, setSessionState] = useState<SessionState>('loading');
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [answerState, setAnswerState] = useState<AnswerState>('unanswered');
  const [results, setResults] = useState<PracticeSessionResult[]>([]);
  const [pendingSavedSession, setPendingSavedSession] = useState<SavedPracticeSession | null>(null);
  const [shouldFetchNew, setShouldFetchNew] = useState(false);
  const questionStartTime = useRef<number>(Date.now());
  const sessionStartTime = useRef<number>(Date.now());

  const questionCount = Math.min(count || 50, 100);
  const sessionKey = `practice_session_${examId}_${categoryId}`;

  const applySavedSession = useCallback((saved: SavedPracticeSession) => {
    setQuestions(saved.questions);
    setCurrentIndex(saved.currentIndex);
    setResults(saved.results);
    setSelectedAnswer(saved.selectedAnswer);
    setAnswerState(saved.answerState);
    sessionStartTime.current = saved.sessionStartTime;
    questionStartTime.current = saved.questionStartTime;

    if (saved.currentIndex >= saved.questions.length) {
      setSessionState('completed');
    } else {
      setSessionState(saved.sessionState);
    }
  }, []);

  const persistSession = useCallback((
    override?: Partial<Pick<SavedPracticeSession, 'questions' | 'currentIndex' | 'results' | 'selectedAnswer' | 'answerState' | 'sessionState'>>,
  ) => {
    const qs = override?.questions ?? questions;
    if (qs.length === 0) return;

    const activeState = override?.sessionState ?? (sessionState === 'showing-result' ? 'showing-result' : 'answering');
    const savedSession: SavedPracticeSession = {
      examId,
      categoryId,
      difficulty,
      count: questionCount,
      questions: qs,
      currentIndex: override?.currentIndex ?? currentIndex,
      results: override?.results ?? results,
      selectedAnswer: override?.selectedAnswer ?? selectedAnswer,
      answerState: override?.answerState ?? answerState,
      sessionState: activeState === 'showing-result' ? 'showing-result' : 'answering',
      sessionStartTime: sessionStartTime.current,
      questionStartTime: questionStartTime.current,
      lastSaved: Date.now(),
    };

    localStorage.setItem(sessionKey, JSON.stringify(savedSession));
  }, [examId, categoryId, difficulty, questionCount, questions, currentIndex, results, selectedAnswer, answerState, sessionState, sessionKey]);

  const loadSavedSession = useCallback((): SavedPracticeSession | null => {
    try {
      const saved = localStorage.getItem(sessionKey);
      if (!saved) return null;

      const parsed = JSON.parse(saved) as Partial<SavedPracticeSession> & SavedPracticeSession;

      const hoursElapsed = (Date.now() - (parsed.lastSaved ?? 0)) / (1000 * 60 * 60);
      if (hoursElapsed > 24) {
        localStorage.removeItem(sessionKey);
        return null;
      }

      return {
        ...parsed,
        selectedAnswer: parsed.selectedAnswer ?? null,
        answerState: parsed.answerState ?? 'unanswered',
        sessionState: parsed.sessionState ?? 'answering',
      };
    } catch {
      localStorage.removeItem(sessionKey);
      return null;
    }
  }, [sessionKey]);

  const sessionMatchesParams = useCallback((saved: SavedPracticeSession) => {
    return saved.count === questionCount && (!difficulty || saved.difficulty === difficulty);
  }, [questionCount, difficulty]);

  // Initial load: check for saved session or fetch new questions
  useEffect(() => {
    if (!categoryId) {
      setQuestions([]);
      setSessionState('completed');
      return;
    }

    if (!shouldFetchNew) {
      const savedSession = loadSavedSession();
      if (savedSession && sessionMatchesParams(savedSession)) {
        setPendingSavedSession(savedSession);
        setSessionState('pending-resume');
        return;
      }
      localStorage.removeItem(sessionKey);
    }

    let cancelled = false;
    setSessionState('loading');
    setPendingSavedSession(null);

    api
      .get(`/questions/category/${categoryId}/random`, {
        params: {
          count: questionCount,
          locale: getContentLocale(),
          ...(difficulty ? { difficulty } : {}),
        },
      })
      .then(({ data }) => {
        if (cancelled) return;
        const mapped = (data as any[]).map(mapQuestion);
        const ordered = shuffle ? shuffleArray(mapped) : mapped;
        setQuestions(ordered);
        setCurrentIndex(0);
        setSelectedAnswer(null);
        setAnswerState('unanswered');
        setResults([]);
        setSessionState(ordered.length > 0 ? 'answering' : 'completed');
        sessionStartTime.current = Date.now();
        questionStartTime.current = Date.now();
        setShouldFetchNew(false);
      })
      .catch(() => {
        if (!cancelled) {
          setQuestions([]);
          setSessionState('completed');
          setShouldFetchNew(false);
        }
      });

    return () => { cancelled = true; };
  }, [categoryId, shuffle, difficulty, count, questionCount, sessionKey, loadSavedSession, sessionMatchesParams, shouldFetchNew]);

  const resumeSavedSession = useCallback(() => {
    if (!pendingSavedSession) return;
    applySavedSession(pendingSavedSession);
    setPendingSavedSession(null);
  }, [pendingSavedSession, applySavedSession]);

  const discardSavedSessionAndRestart = useCallback(() => {
    localStorage.removeItem(sessionKey);
    setPendingSavedSession(null);
    setShouldFetchNew(true);
  }, [sessionKey]);

  const saveForLater = useCallback(() => {
    persistSession();
  }, [persistSession]);

  const clearSavedSession = useCallback(() => {
    localStorage.removeItem(sessionKey);
  }, [sessionKey]);

  const isSessionInProgress = sessionState === 'answering' || sessionState === 'showing-result';

  const currentQuestion = questions[currentIndex] ?? null;

  const selectAnswer = useCallback(
    (index: number) => {
      if (sessionState !== 'answering') return;
      setSelectedAnswer(index);
    },
    [sessionState],
  );

  const submitAnswer = useCallback(() => {
    if (selectedAnswer === null || !currentQuestion || sessionState !== 'answering') return;

    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
    const timeSpent = Math.floor((Date.now() - questionStartTime.current) / 1000);
    const newResults = [
      ...results,
      {
        questionId: currentQuestion.id,
        selectedAnswer,
        correctAnswer: currentQuestion.correctAnswer,
        isCorrect,
        timeSpent,
      },
    ];

    setAnswerState(isCorrect ? 'correct' : 'incorrect');
    setResults(newResults);
    setSessionState('showing-result');

    persistSession({
      results: newResults,
      selectedAnswer,
      answerState: isCorrect ? 'correct' : 'incorrect',
      sessionState: 'showing-result',
    });
  }, [selectedAnswer, currentQuestion, sessionState, results, persistSession]);

  const nextQuestion = useCallback(() => {
    if (currentIndex >= questions.length - 1) {
      setSessionState('completed');
      localStorage.removeItem(sessionKey);
      return;
    }

    const nextIndex = currentIndex + 1;
    setCurrentIndex(nextIndex);
    setSelectedAnswer(null);
    setAnswerState('unanswered');
    setSessionState('answering');
    questionStartTime.current = Date.now();

    persistSession({
      currentIndex: nextIndex,
      selectedAnswer: null,
      answerState: 'unanswered',
      sessionState: 'answering',
    });
  }, [currentIndex, questions.length, persistSession, sessionKey]);

  const restartSession = useCallback(() => {
    const reordered = shuffle ? shuffleArray(questions) : questions;
    setQuestions(reordered);
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setAnswerState('unanswered');
    setResults([]);
    setSessionState('answering');
    sessionStartTime.current = Date.now();
    questionStartTime.current = Date.now();
    localStorage.removeItem(sessionKey);
  }, [questions, shuffle, sessionKey]);

  const score =
    results.length > 0
      ? Math.round((results.filter((r) => r.isCorrect).length / results.length) * 100)
      : 0;

  const correctCount = results.filter((r) => r.isCorrect).length;
  const totalTimeElapsed = Math.floor((Date.now() - sessionStartTime.current) / 1000);
  const totalPoints = results.reduce((sum, r, i) => {
    return sum + (r.isCorrect ? (questions[i]?.points ?? 1) : 0);
  }, 0);

  return {
    currentQuestion,
    currentIndex,
    totalQuestions: questions.length,
    sessionState,
    selectedAnswer,
    answerState,
    results,
    questions,
    pendingSavedSession,
    isSessionInProgress,
    selectAnswer,
    submitAnswer,
    nextQuestion,
    restartSession,
    resumeSavedSession,
    discardSavedSessionAndRestart,
    saveForLater,
    clearSavedSession,
    score,
    correctCount,
    totalTimeElapsed,
    totalPoints,
  };
}
